import { Link } from 'react-router-dom';
import {
  GraduationCap,
  MessageSquare,
  Users,
  Share2,
  BookOpen,
  Sparkles,
  Star,
  Check,
  ArrowRight,
  ChevronRight,
  ClipboardList,
  Brain,
  FileText
} from 'lucide-react';

function Landing() {
  const features = [
    {
      icon: Brain,
      title: 'AI Doubt Solver',
      description: 'Ask any exam doubt and get instant step-by-step explanations. Upload images of questions and get solutions in seconds.'
    },
    {
      icon: FileText,
      title: 'AI Notebooks',
      description: 'Upload your textbooks and study materials. Generate quizzes, flashcards, mind maps, and summaries powered by AI.'
    },
    {
      icon: ClipboardList,
      title: 'Mock Tests',
      description: 'Real PGCET previous year papers (2021-2025) and AI-generated full-length mock tests for PGCET and MAT with section-wise practice.'
    },
    {
      icon: BookOpen,
      title: 'Study Materials',
      description: 'Access curated study materials for MBA entrance exams. Reference guides, formula sheets, and topic-wise notes for PGCET and MAT.'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Join discussion channels, connect with fellow aspirants, and learn together in a supportive community.'
    },
    {
      icon: MessageSquare,
      title: 'Direct Messaging',
      description: 'Private conversations with mentors and peers for personalized guidance and exam strategy support.'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Create Your Account',
      description: 'Sign up in seconds and get instant access to all features. No setup required.'
    },
    {
      number: '02',
      title: 'Choose Your Exam',
      description: 'Select your target MBA entrance exam — PGCET or MAT. Access real PYQ papers and AI-generated mock tests instantly.'
    },
    {
      number: '03',
      title: 'Start Preparing',
      description: 'Use AI doubt solver, take mock tests, study materials, create AI notebooks — everything you need to crack your exam.'
    }
  ];

  const exams = [
    { name: 'PGCET', exams: 'Karnataka PGCET — 100 Qs, 90 min. Real PYQ papers (2021-2025) + AI practice mode.', color: 'bg-purple-50 text-purple-700' },
    { name: 'MAT', exams: 'Management Aptitude Test — 200 Qs, 150 min. AI-generated full-length mock tests.', color: 'bg-blue-50 text-blue-700' }
  ];

  const testimonials = [
    {
      name: 'Priya Sharma',
      role: 'PGCET Aspirant',
      content: 'Practicing with real PGCET previous year papers from 2021-2025 gave me the exact exam feel. The AI doubt solver helped me clear concepts instantly.',
      rating: 5
    },
    {
      name: 'Rahul Verma',
      role: 'MAT Aspirant',
      content: 'The AI-generated MAT mock tests cover all 5 sections perfectly. Section-wise practice and instant explanations made my prep much smarter.',
      rating: 5
    },
    {
      name: 'Ananya Patel',
      role: 'PGCET Aspirant',
      content: 'I love how I can generate fresh practice papers anytime with different difficulty levels. The AI notebooks turned my study materials into quizzes effortlessly.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <GraduationCap className="w-8 h-8 text-primary-600" />
              <span className="text-xl font-bold text-gray-900">ExamPrep</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-primary-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4 mr-2" />
              AI-Powered Exam Preparation
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Crack PGCET & MAT with
              <span className="text-primary-600"> Confidence</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Your all-in-one MBA entrance prep platform for PGCET and MAT. Real previous year papers,
              AI-generated mock tests, doubt solving, smart notebooks, and curated study materials.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors shadow-lg shadow-primary-600/20"
              >
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/login"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-white text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors border border-gray-200"
              >
                Sign In
              </Link>
            </div>
          </div>

          {/* Hero Visual */}
          <div className="mt-16 relative">
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl shadow-2xl p-8 max-w-4xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-inner">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1 bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700">
                      Newton's Third Law states that for every action, there is an equal and opposite
                      reaction. For example, when you push against a wall, the wall pushes back with
                      equal force. Would you like me to explain more with numerical problems?
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 flex-row-reverse">
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 ml-4">
                    <span className="text-sm font-medium text-gray-600">You</span>
                  </div>
                  <div className="flex-1 bg-primary-50 rounded-lg p-4">
                    <p className="text-gray-700">
                      Yes please! Can you give me a JEE-level problem based on Newton's Third Law?
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exam Categories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              MBA Entrance Exams <span className="text-primary-600">Covered</span>
            </h2>
            <p className="mt-3 text-lg text-gray-600">Focused preparation for PGCET and MAT</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {exams.map((exam, i) => (
              <div key={i} className={`${exam.color} rounded-xl p-4 text-center`}>
                <h3 className="font-bold text-lg">{exam.name}</h3>
                <p className="text-xs mt-1 opacity-80">{exam.exams}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Everything You Need to
              <span className="text-primary-600"> Ace Your Exam</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Powerful AI tools and comprehensive resources to help you prepare smarter.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-lg hover:border-primary-100 transition-all group"
              >
                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-100 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              How It Works
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Get started in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                  <div className="text-5xl font-bold text-primary-100 mb-4">{step.number}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ChevronRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Loved by Students
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              See what our learners have to say
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm"
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6">"{testimonial.content}"</p>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-700 font-semibold">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-3xl p-12 text-center shadow-xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Crack Your Dream Exam?
            </h2>
            <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of aspirants preparing smarter with AI.
              Start now and ace your exam.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 rounded-xl font-semibold hover:bg-primary-50 transition-colors shadow-lg"
            >
              Get Started
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <GraduationCap className="w-8 h-8 text-primary-500" />
              <span className="text-xl font-bold text-white">ExamPrep</span>
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/login" className="hover:text-white transition-colors">
                Sign In
              </Link>
              <Link to="/register" className="hover:text-white transition-colors">
                Register
              </Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} ExamPrep. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
